import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';
import {
  isOfflineStorageAvailable,
  saveTripSnapshot,
  getTripSnapshot,
  deleteTripSnapshot,
} from '../utils/offlineTripStore';

const PARTNER_AUTH = { usePartnerAuth: true };

/**
 * Partner-seat offline cache for a managed trip (shared itinerary only).
 * Storage key uses partnerSeatId (e.g. partnerUser:12 or partner:9).
 */
const useOfflinePartnerTrip = (tripId, partnerSeatId) => {
  const [snapshot, setSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState(false);
  const isMountedRef = useRef(true);
  const supported = isOfflineStorageAvailable();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!supported || !tripId || !partnerSeatId) {
      setSnapshot(null);
      setChecked(true);
      return undefined;
    }

    let cancelled = false;
    getTripSnapshot(partnerSeatId, tripId)
      .then((existing) => {
        if (!cancelled && isMountedRef.current) setSnapshot(existing);
      })
      .catch(() => {
        if (!cancelled && isMountedRef.current) setSnapshot(null);
      })
      .finally(() => {
        if (!cancelled && isMountedRef.current) setChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [supported, tripId, partnerSeatId]);

  const save = useCallback(async () => {
    if (!supported || !tripId || !partnerSeatId) {
      setError('unsupported');
      return null;
    }
    setSaving(true);
    setError(null);
    try {
      const [tripResponse, itineraryResponse] = await Promise.all([
        api.get(`/trips/${tripId}`, PARTNER_AUTH),
        api.get(`/trips/${tripId}/itinerary`, PARTNER_AUTH),
      ]);
      const saved = await saveTripSnapshot({
        userId: partnerSeatId,
        tripId,
        trip: tripResponse.data,
        itinerary: itineraryResponse.data || [],
        memberItinerary: [],
      });
      if (isMountedRef.current) setSnapshot(saved);
      return saved;
    } catch (err) {
      if (isMountedRef.current) setError(err.response ? 'failed' : 'offline');
      return null;
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [supported, tripId, partnerSeatId]);

  const remove = useCallback(async () => {
    if (!supported || !tripId || !partnerSeatId) return;
    try {
      await deleteTripSnapshot(partnerSeatId, tripId);
      if (isMountedRef.current) {
        setSnapshot(null);
        setError(null);
      }
    } catch {
      if (isMountedRef.current) setError('failed');
    }
  }, [supported, tripId, partnerSeatId]);

  return {
    supported,
    snapshot,
    isSaved: Boolean(snapshot),
    savedAt: snapshot?.savedAt || null,
    saving,
    error,
    checked,
    save,
    remove,
  };
};

export default useOfflinePartnerTrip;
